import { Injectable, inject } from '@angular/core';
import { Key } from '../db/ngx-indexed-db.meta';
import { SiaNgxIndexedDBService } from '../db/ngx-indexed-db.service';
import { SiaCacheData } from '../interfaces/sia-cache-data';

@Injectable({
  providedIn: 'root',
})
export class SiaIndexedDbService {
  protected dbService = inject(SiaNgxIndexedDBService);

  private async execAdd(store: string, key: string, items: any): Promise<boolean> {
    if (!key) return false;
    const cData: SiaCacheData = { key, value: items };
    try {
      const exists = await this.exists(store, key);
      if (exists) {
        await this.update(store, cData);
      } else {
        await this.add(store, cData);
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  protected async getCacheDataValue(store: string, key: string): Promise<any> {
    const cData: SiaCacheData = await this.getById(store, key);
    return cData?.value ?? null;
  }

  protected async setCacheDataValue(store: string, key: string, items: any): Promise<boolean> {
    return this.execAdd(store, key, items);
  }

  protected async clearRecordsContainingKeys(
    store: string,
    concatenatedKeys: string | undefined,
  ): Promise<boolean> {
    if (!concatenatedKeys) return false;
    const keysArray = concatenatedKeys.split('|').filter(Boolean);
    if (keysArray.length === 0) return false;

    const items: unknown[] = await this.dbService.getAll(store);
    if (!items || items.length === 0) return false;

    const toDelete: SiaCacheData[] = [];
    for (const k of keysArray) {
      const lowerK = k.toLowerCase();
      for (const item of items) {
        const cacheData = item as SiaCacheData;
        if (
          cacheData?.key &&
          cacheData.key.toString().toLowerCase().includes(lowerK)
        ) {
          toDelete.push(cacheData);
        }
      }
    }

    if (toDelete.length === 0) return false;

    await Promise.all(toDelete.map((item) => this.deleteByKey(store, item.key)));
    return true;
  }

  getCount(store: string): Promise<number> {
    return this.dbService.count(store);
  }

  async exists(store: string, key: string): Promise<boolean> {
    const item = await this.dbService.getByKey(store, key);
    return !!item;
  }

  async add(store: string, data: any, key?: string): Promise<boolean> {
    try {
      await this.dbService.add(store, data, key);
      return true;
    } catch {
      return false;
    }
  }

  bulkAdd<T>(store: string, items: any) {
    return this.dbService.bulkAdd(store, items);
  }

  async update(store: string, data: any, key?: string): Promise<any> {
    await this.dbService.update(store, data, key);
    return data;
  }

  async updateCacheData(store: string, cData: SiaCacheData): Promise<SiaCacheData> {
    await this.dbService.update(store, cData);
    return cData;
  }

  async recreateDatabase(): Promise<void> {
    await this.dbService.deleteDatabase();
    this.dbService.createDataBase();
  }

  async getById(store: string, key: string): Promise<any> {
    return this.dbService.getByID<any>(store, key);
  }

  protected getAll<T>(store: string) {
    return this.dbService.getAll<T>(store);
  }

  protected clearStore(store: string) {
    return this.dbService.clear(store);
  }

  delete<T>(store: string, key: Key): Promise<T[]> {
    return this.dbService.delete<T>(store, key);
  }

  deleteByKey(store: string, key: Key | undefined): Promise<boolean> {
    if (!key) return Promise.resolve(false);
    return this.dbService.deleteByKey(store, key);
  }
}
